/**
 * Structured Site Editor — main page.
 *
 * Layout:
 *  - Top bar: site name + draft/published badges + save state + Preview + Publish
 *  - Left: panel switcher (Sections / Branding / Navigation / Footer) + Pages list
 *  - Center: the active panel
 *  - Right: real preview iframe + capabilities
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {sitesApi} from '../lib/api-resources';
import {useAuth} from '../lib/auth';
import type {
  EditorBrandingPayload,
  EditorFooterPayload,
  EditorNavigationPayload,
  EditorPage,
  EditorSection,
  EditorSectionType,
  EditorSnapshot,
} from '../lib/types';
import {SECTION_TYPE_LABELS, labelOf, PAGE_ROLE_LABELS} from '../lib/ui-labels';
import {friendlyError} from '../lib/error-labels';
import SectionEditor from '../components/SectionEditor';
import BrandingPanel from '../components/EditorPanels/BrandingPanel';
import NavigationPanel from '../components/EditorPanels/NavigationPanel';
import FooterPanel from '../components/EditorPanels/FooterPanel';
import PublishSummaryModal from '../components/EditorPanels/PublishSummaryModal';
import './SiteEditorPage.css';

const SECTION_LABELS: Record<EditorSectionType, string> = SECTION_TYPE_LABELS as Record<EditorSectionType, string>;

type PanelKind = 'sections' | 'branding' | 'navigation' | 'footer';
type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

const WP_PUBLIC_BASE = 'https://phase4-test.wujiong.cn';
const WP_PREVIEW_URL = `${WP_PUBLIC_BASE}/wp-json/wujiong/v1/preview`;

export default function SiteEditorPage() {
  const {siteId} = useParams<{siteId: string}>();
  const {token} = useAuth();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<EditorSnapshot | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [panel, setPanel] = useState<PanelKind>('sections');
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [status, setStatus] = useState<{kind: 'idle' | 'info' | 'error'; msg?: string}>({kind: 'idle'});
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [conflictInfo, setConflictInfo] = useState<{expected: number; actual: number} | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const previewFormRef = useRef<HTMLFormElement | null>(null);

  // ---- load
  const loadSnapshot = useCallback(async () => {
    if (!siteId) return;
    try {
      const snap = await sitesApi.editor.snapshot(siteId);
      setSnapshot(snap);
      setSelectedPageId((p) => p ?? snap.pages[0]?.id ?? null);
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
    }
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !token) return;
    void loadSnapshot();
  }, [siteId, token, loadSnapshot]);

  // ---- unsaved changes prompt
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (saveState === 'dirty' || saveState === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  // ---- error helpers
  const isRevisionConflict = (e: any): boolean =>
    Boolean(e && typeof e === 'object' && 'status' in e && (e as {status: number}).status === 409);

  // ---- preview
  const onPreview = useCallback(async () => {
    if (!siteId) return;
    try {
      const tok = await sitesApi.editor.previewToken(siteId);
      setPreviewToken(tok.token);
      setStatus({kind: 'info', msg: '预览已生成，请查看右侧窗口。'});
      setTimeout(() => {
        previewFormRef.current?.submit();
      }, 50);
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
    }
  }, [siteId]);

  const onPublishClick = () => setShowPublishModal(true);
  const onPublishConfirm = useCallback(async () => {
    if (!siteId) return;
    setIsPublishing(true);
    setStatus({kind: 'info', msg: '正在发布…'});
    try {
      await sitesApi.editor.publish(siteId);
      setStatus({kind: 'info', msg: '发布任务已提交，请查看进度。'});
      let attempts = 0;
      const pollTimer = window.setInterval(async () => {
        attempts += 1;
        try {
          const fresh = await sitesApi.editor.snapshot(siteId);
          setSnapshot(fresh);
          if (!fresh.is_publishing || attempts > 30) {
            window.clearInterval(pollTimer);
            setIsPublishing(false);
            setShowPublishModal(false);
            setStatus({
              kind: 'info',
              msg: fresh.is_publishing
                ? '仍在发布中，请稍候…'
                : '发布完成，线上站点已更新。',
            });
          }
        } catch (e: any) {
          window.clearInterval(pollTimer);
          setIsPublishing(false);
          const f = friendlyError(e?.code, e?.message);
          setStatus({kind: 'error', msg: f.headline});
        }
      }, 2000);
    } catch (e: any) {
      setIsPublishing(false);
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
    }
  }, [siteId]);

  // ---- section helpers
  const updateSectionInLocal = (sec: EditorSection) => {
    if (!snapshot) return;
    setSnapshot({
      ...snapshot,
      pages: snapshot.pages.map((p) => {
        if (p.id !== sec.page_id) return p;
        return {
          ...p,
          sections: p.sections.map((s) => (s.id === sec.id ? sec : s)),
        };
      }),
    });
    setSaveState('dirty');
  };

  const updateSection = async (sec: EditorSection) => {
    if (!siteId || !snapshot) return;
    setSaveState('saving');
    try {
      await sitesApi.editor.updateSection(
        siteId,
        sec.id,
        {
          client_id: sec.client_id,
          type: sec.type,
          variant: sec.variant,
          enabled: sec.enabled,
          data: sec.data,
        },
        snapshot.draft_revision
      );
      const fresh = await sitesApi.editor.snapshot(siteId);
      setSnapshot(fresh);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
      setSaveState('error');
    }
  };

  const addSection = async (type: EditorSectionType) => {
    if (!siteId || !snapshot || !selectedPageId) return;
    const clientId = `sec-${type}-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
    const defaultData: Record<string, unknown> = (() => {
      switch (type) {
        case 'hero': return {title: '新首屏', align: 'left'};
        case 'features': return {title: '核心优势', items: [{title: '优势 1'}]};
        case 'about': return {title: '关于我们', content: '向访客介绍你的业务。'};
        case 'products': return {title: '产品 / 服务', items: [{title: '产品 1'}]};
        case 'cta': return {title: '立即开始', cta_label: '联系我们', cta_url: ''};
        case 'faq': return {title: '常见问题', items: [{question: '常见问题？', answer: '回答。'}]};
      }
    })();
    setSaveState('saving');
    try {
      const sec = await sitesApi.editor.createSection(
        siteId, selectedPageId,
        {client_id: clientId, type, variant: `${type}-01`, enabled: true, data: defaultData},
        snapshot.draft_revision
      );
      const fresh = await sitesApi.editor.snapshot(siteId);
      setSnapshot(fresh);
      setSelectedSectionId(sec.id);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
      setSaveState('error');
    }
  };

  const removeSection = async (sec: EditorSection) => {
    if (!siteId || !snapshot) return;
    if (!window.confirm(`确定删除「${SECTION_LABELS[sec.type]}」区块吗？`)) return;
    setSaveState('saving');
    try {
      await sitesApi.editor.deleteSection(siteId, sec.id, snapshot.draft_revision);
      const fresh = await sitesApi.editor.snapshot(siteId);
      setSnapshot(fresh);
      if (selectedSectionId === sec.id) setSelectedSectionId(null);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
      setSaveState('error');
    }
  };

  const toggleEnabled = async (sec: EditorSection) => {
    const next: EditorSection = {...sec, enabled: !sec.enabled};
    if (!siteId || !snapshot) return;
    setSaveState('saving');
    try {
      await sitesApi.editor.updateSection(
        siteId, sec.id,
        {client_id: next.client_id, type: next.type, variant: next.variant, enabled: next.enabled, data: next.data},
        snapshot.draft_revision
      );
      const fresh = await sitesApi.editor.snapshot(siteId);
      setSnapshot(fresh);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
      setSaveState('error');
    }
  };

  const moveSection = async (sec: EditorSection, dir: -1 | 1) => {
    if (!siteId || !snapshot || !selectedPageId) return;
    const page = snapshot.pages.find((p) => p.id === selectedPageId);
    if (!page) return;
    const sorted = [...page.sections].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((s) => s.id === sec.id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
    const ids = sorted.map((s) => s.id);
    setSaveState('saving');
    try {
      await sitesApi.editor.reorderSections(siteId, ids, snapshot.draft_revision);
      const fresh = await sitesApi.editor.snapshot(siteId);
      setSnapshot(fresh);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      const f = friendlyError(e?.code, e?.message);
      setStatus({kind: 'error', msg: f.headline});
      setSaveState('error');
    }
  };

  // ---- branding / nav / footer save handlers
  const saveBranding = async (b: EditorBrandingPayload) => {
    if (!siteId || !snapshot) return;
    setSaveState('saving');
    try {
      const fresh = await sitesApi.editor.saveBranding(siteId, b, snapshot.draft_revision);
      setSnapshot(fresh as EditorSnapshot);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      throw e;
    }
  };

  const saveNavigation = async (n: EditorNavigationPayload) => {
    if (!siteId || !snapshot) return;
    setSaveState('saving');
    try {
      const fresh = await sitesApi.editor.saveNavigation(siteId, n, snapshot.draft_revision);
      setSnapshot(fresh as EditorSnapshot);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      throw e;
    }
  };

  const saveFooter = async (f: EditorFooterPayload) => {
    if (!siteId || !snapshot) return;
    setSaveState('saving');
    try {
      const fresh = await sitesApi.editor.saveFooter(siteId, f, snapshot.draft_revision);
      setSnapshot(fresh as EditorSnapshot);
      setSaveState('saved');
      setTimeout(() => setSaveState('clean'), 1500);
    } catch (e: any) {
      if (isRevisionConflict(e)) {
        setConflictInfo({expected: snapshot.draft_revision, actual: snapshot.draft_revision + 1});
        setSaveState('error');
        await loadSnapshot();
        return;
      }
      throw e;
    }
  };

  // ---- loading state
  if (!snapshot) {
    return (
      <div className="site-editor">
        <div className="editor-loading">正在加载编辑器…</div>
        {status.kind === 'error' && <div className="editor-error">{status.msg}</div>}
      </div>
    );
  }

  const caps = snapshot.capabilities;
  const isUnsynced = snapshot.draft_revision > snapshot.published_revision;
  const selectedPage = snapshot.pages.find((p) => p.id === selectedPageId);
  const saveLabel: Record<SaveState, string> = {
    clean: isUnsynced ? '已保存（未发布）' : '已保存',
    dirty: '有未保存的修改',
    saving: '保存中…',
    saved: '已保存 ✓',
    error: '保存失败',
  };
  const saveClass: Record<SaveState, string> = {
    clean: isUnsynced ? 'dirty' : 'clean',
    dirty: 'dirty',
    saving: 'saving',
    saved: 'saved',
    error: 'error',
  };

  return (
    <div className="site-editor">
      {/* Top bar */}
      <header className="editor-topbar">
        <button className="editor-back" onClick={() => navigate('/sites')}>
          ← 返回站点
        </button>
        <div className="editor-title">
          <strong>站点编辑器</strong>
          <span className="editor-site-id">#{snapshot.site_id.slice(0, 8)}</span>
          <span className="editor-template">
            模板：{snapshot.template_id}@{snapshot.template_version}
          </span>
        </div>
        <div className="editor-revisions">
          <span className={`revision-badge ${isUnsynced ? 'dirty' : 'clean'}`}>
            草稿 v{snapshot.draft_revision}
          </span>
          <span className="revision-badge">已发布 v{snapshot.published_revision}</span>
          {snapshot.published_at && (
            <span className="editor-published-at">
              最近发布：{new Date(snapshot.published_at).toLocaleString()}
            </span>
          )}
          <span className={`save-badge ${saveClass[saveState]}`}>
            {saveLabel[saveState]}
          </span>
        </div>
        <div className="editor-actions">
          <button className="btn-secondary" onClick={onPreview} disabled={isPublishing}>
            预览
          </button>
          <button
            className="btn-primary"
            onClick={onPublishClick}
            disabled={isPublishing || snapshot.is_publishing}
          >
            {isPublishing || snapshot.is_publishing ? '发布中…' : '发布网站'}
          </button>
        </div>
      </header>

      {status.kind === 'error' && <div className="editor-error">{status.msg}</div>}
      {status.kind === 'info' && <div className="editor-info">{status.msg}</div>}

      {conflictInfo && (
        <div className="editor-conflict">
          <strong>页面已被其他操作更新。</strong>
          <span>你正在编辑的版本 v{conflictInfo.expected} 已过期，最新草稿是 v{conflictInfo.actual}，请刷新页面加载最新内容。</span>
          <button className="btn-secondary" onClick={() => setConflictInfo(null)}>知道了</button>
        </div>
      )}

      <div className="editor-body">
        {/* Left: panel switcher + pages list */}
        <aside className="editor-sidebar">
          <nav className="panel-tabs">
            <button
              className={panel === 'sections' ? 'active' : ''}
              onClick={() => setPanel('sections')}
            >区块</button>
            <button
              className={panel === 'branding' ? 'active' : ''}
              onClick={() => setPanel('branding')}
            >品牌</button>
            <button
              className={panel === 'navigation' ? 'active' : ''}
              onClick={() => setPanel('navigation')}
            >导航</button>
            <button
              className={panel === 'footer' ? 'active' : ''}
              onClick={() => setPanel('footer')}
            >页脚</button>
          </nav>

          {panel === 'sections' && (
            <div className="editor-pages">
              <h3>页面</h3>
              <ul>
                {snapshot.pages.map((p: EditorPage) => (
                  <li
                    key={p.id}
                    className={selectedPageId === p.id ? 'active' : ''}
                    onClick={() => { setSelectedPageId(p.id); setSelectedSectionId(null); }}
                  >
                    <span className="role-pill">{labelOf(PAGE_ROLE_LABELS, p.role, p.role)}</span>
                    <span className="title">{p.title}</span>
                    <span className="slug">/{p.slug}</span>
                  </li>
                ))}
              </ul>
              {snapshot.pages.length === 0 && (
                <p className="empty-hint">还没有页面。</p>
              )}
            </div>
          )}
        </aside>

        {/* Center: active panel */}
        <main className="editor-main">
          {panel === 'sections' && (
            <>
              {!selectedPage ? (
                <p className="empty-hint">请先在左侧选择一个页面。</p>
              ) : (
                <>
                  <h2>
                    {selectedPage.title} <small>（{labelOf(PAGE_ROLE_LABELS, selectedPage.role, selectedPage.role)}）</small>
                  </h2>
                  <ol className="section-list">
                    {selectedPage.sections
                      .slice()
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((sec, i, arr) => (
                        <li
                          key={sec.id}
                          className={[
                            'section-item',
                            selectedSectionId === sec.id ? 'selected' : '',
                            sec.enabled ? '' : 'disabled',
                          ].join(' ')}
                        >
                          <div className="section-header">
                            <span className="section-type">{SECTION_LABELS[sec.type]}</span>
                            <span className="section-variant">{sec.variant}</span>
                            <span className="section-source">{sec.source}</span>
                            <span className="section-spacer" />
                            <button
                              className="icon-btn"
                              onClick={() => moveSection(sec, -1)}
                              disabled={i === 0}
                              title="上移"
                            >↑</button>
                            <button
                              className="icon-btn"
                              onClick={() => moveSection(sec, 1)}
                              disabled={i === arr.length - 1}
                              title="下移"
                            >↓</button>
                            <label className="toggle">
                              <input
                                type="checkbox"
                                checked={sec.enabled}
                                onChange={() => toggleEnabled(sec)}
                              />
                              {sec.enabled ? '启用' : '停用'}
                            </label>
                            <button
                              className="icon-btn"
                              onClick={() => setSelectedSectionId(sec.id)}
                            >编辑</button>
                            <button
                              className="icon-btn danger"
                              onClick={() => removeSection(sec)}
                            >删除</button>
                          </div>
                          {selectedSectionId === sec.id && siteId && (
                            <SectionEditor
                              section={sec}
                              onChange={updateSectionInLocal}
                              onSave={(next) => updateSection(next)}
                              capabilities={caps}
                              siteId={siteId}
                              expectedRevision={snapshot.draft_revision}
                            />
                          )}
                        </li>
                      ))}
                  </ol>
                  {selectedPage.sections.length === 0 && (
                    <p className="empty-hint">当前页面还没有区块。</p>
                  )}
                  <div className="add-section">
                    <span>添加区块：</span>
                    {(Object.keys(SECTION_LABELS) as EditorSectionType[])
                      .filter((t) => caps.supports[t] !== false)
                      .map((t) => (
                        <button
                          key={t}
                          className="btn-secondary"
                          onClick={() => addSection(t)}
                          disabled={isPublishing}
                        >
                          + {SECTION_LABELS[t]}
                        </button>
                      ))}
                  </div>
                </>
              )}
            </>
          )}

          {panel === 'branding' && siteId && (
            <BrandingPanel
              initial={snapshot.branding as unknown as EditorBrandingPayload}
              onSave={saveBranding}
              onDirty={() => setSaveState('dirty')}
              saving={saveState === 'saving'}
              siteId={siteId}
            />
          )}

          {panel === 'navigation' && siteId && (
            <NavigationPanel
              initial={snapshot.navigation as unknown as EditorNavigationPayload}
              pages={snapshot.pages}
              onSave={saveNavigation}
              onDirty={() => setSaveState('dirty')}
              saving={saveState === 'saving'}
            />
          )}

          {panel === 'footer' && siteId && (
            <FooterPanel
              initial={snapshot.footer as unknown as EditorFooterPayload}
              onSave={saveFooter}
              onDirty={() => setSaveState('dirty')}
              saving={saveState === 'saving'}
            />
          )}
        </main>

        {/* Right: preview iframe + capabilities */}
        <aside className="editor-preview">
          <h3>预览</h3>
          {previewToken ? (
            <div className="preview-frame-wrap">
              <iframe
                title="实时预览"
                name="wujiong-preview"
                src={WP_PUBLIC_BASE}
                className="preview-iframe"
              />
            </div>
          ) : (
            <p className="empty-hint">
              点击上方的「预览」按钮即可在右侧窗口查看当前草稿。线上站点会保持最近一次发布的版本，直到你点击「发布网站」。
            </p>
          )}
          <h3>模板能力</h3>
          <ul className="caps-list">
            {Object.entries(caps.supports).map(([k, v]) => (
              <li key={k} className={v ? 'on' : 'off'}>
                {k} {v ? '✓' : '—'}
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {/* Hidden form for preview POST */}
      <form
        ref={previewFormRef}
        method="POST"
        action={WP_PREVIEW_URL}
        target="wujiong-preview"
        style={{display: 'none'}}
      >
        {previewToken && (
          <input type="hidden" name="token" value={previewToken} />
        )}
      </form>

      {showPublishModal && (
        <PublishSummaryModal
          snapshot={snapshot}
          onConfirm={onPublishConfirm}
          onClose={() => setShowPublishModal(false)}
          publishing={isPublishing}
        />
      )}
    </div>
  );
}
